import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
type TransactionType = "domestic" | "international" | "ach";

@Injectable({
  providedIn: "root",
})
export class StripeService {
  url = `${environment.apiUrl}`;
  authUrl = `${environment.authUrl}`;
  siteUrl = `${environment.siteUrl}`;
  apiKey = environment.stripe_public_key;
  stripe_api = `https://api.stripe.com/v1`;
  stripeHeaders = new HttpHeaders({
    Authorization: `Bearer ${this.apiKey}`,
    "Content-Type": "application/x-www-form-urlencoded",
  });
  constructor(private http: HttpClient) {}

  /*==============================
create Token 
================================*/
  createToken(data: any) {
    const body = new URLSearchParams();
    let headers = this.stripeHeaders;
    body.set("card[name]", data.card.name);

    body.set("card[number]", this.normalizeCardNumber(data.card.number));
    body.set("card[exp_month]", data.card.exp_month);
    body.set("card[exp_year]", data.card.exp_year);
    body.set("card[cvc]", data.card.cvc);

    return this.http.post(`${this.stripe_api}/tokens`, body.toString(), {
      headers,
      observe: "response",
    });
  }
  /*==============================
create PM Token 
================================*/
  createMethode(data: any) {
    const body = new URLSearchParams();
    body.set("type", "card");
    body.set("card[number]", this.normalizeCardNumber(data.card.number));
    body.set("card[exp_month]", data.card.exp_month);
    body.set("card[exp_year]", data.card.exp_year);
    body.set("card[cvc]", data.card.cvc);
    let headers = this.stripeHeaders;
    return this.http.post(`${this.stripe_api}/payment_methods`, body.toString(), {
      headers,
      observe: "response",
    });
  }
  /*==============================
add Card 
================================*/
  addCard(data: any) {
    return this.http.post(`${this.authUrl}/frontoffice/stripe/customer`, data, {
      observe: "response",
    });
  }
  /*==============================
get Card 
================================*/
  getCard() {
    return this.http.post(`${this.authUrl}/frontoffice/user/cards`, {
      observe: "response",
    });
  }
  /*==============================
drop Card 
================================*/
  dropCard(cardId: number) {
    let payload = {
      body: { card_id: cardId },
    };
    return this.http.delete(
      `${this.authUrl}/frontoffice/user/delete/cards`,
      payload
    );
  }
  /*==============================
drop Card 
================================*/
  updateCard(data: { card_id: string; is_default: number; name: string }) {
    let payload = {
      ...data,
    };
    return this.http.post(`${this.authUrl}/frontoffice/update-card`, payload);
  }
  /*==============================
get Card Type 
================================*/
  private readonly cardLengths: Record<string, number[]> = {
    Visa: [16],
    Mastercard: [16],
    Amex: [15],
    Diners: [14],
    Discover: [16],
    Jcb: [15, 16],
    Unionpay: [16, 17, 18, 19],
  };

  private normalizeCardNumber(value: string): string {
    return (value || "").replace(/\D/g, "");
  }

  isValidCardNumber(cardNumber: string): boolean {
    const digits = this.normalizeCardNumber(cardNumber);
    if (!digits) {
      return false;
    }

    const brand = this.detectCardBrand(digits);
    if (brand === "unknown") {
      return false;
    }

    const allowedLengths = this.cardLengths[brand];
    if (!allowedLengths?.includes(digits.length)) {
      return false;
    }

    return this.passesLuhnCheck(digits);
  }

  getCardType(cardNumber: string): string {
    return this.detectCardBrand(this.normalizeCardNumber(cardNumber));
  }

  private detectCardBrand(digits: string): string {
    if (!digits) {
      return "unknown";
    }
    if (/^4/.test(digits)) {
      return "Visa";
    }
    if (/^5[1-5]/.test(digits)) {
      return "Mastercard";
    }
    if (/^3[47]/.test(digits)) {
      return "Amex";
    }
    if (/^3(?:0[0-5]|[68])/.test(digits)) {
      return "Diners";
    }
    if (/^6(?:011|5)/.test(digits)) {
      return "Discover";
    }
    if (/^(?:2131|1800|35)/.test(digits)) {
      return "Jcb";
    }
    if (/^62/.test(digits)) {
      return "Unionpay";
    }
    return "unknown";
  }

  private passesLuhnCheck(digits: string): boolean {
    let sum = 0;
    let shouldDouble = false;

    for (let i = digits.length - 1; i >= 0; i--) {
      let digit = Number.parseInt(digits[i], 10);
      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      sum += digit;
      shouldDouble = !shouldDouble;
    }

    return sum % 10 === 0;
  }

  calculateStripeFee(
    transactionAmount: number,
    transactionType: TransactionType = "domestic"
  ): string {
    let feePercentage: number;
    let fixedFee: number = 0.3;

    switch (transactionType) {
      case "domestic":
        feePercentage = 2.9 / 100;
        break;
      case "international":
        feePercentage = 3.9 / 100;
        break;
      case "ach":
        feePercentage = 0.8 / 100;
        fixedFee = 0.0;
        break;
      default:
        throw new Error("Unsupported transaction type");
    }

    let fee = transactionAmount * feePercentage + fixedFee;

    // For ACH transactions, cap the fee at $5
    if (transactionType === "ach" && fee > 5.0) {
      fee = 5.0;
    }
    return fee.toFixed(2);
  }
}
